import type { ReactNode } from 'react'

import { Badge, Tooltip } from '@mantine/core'

import CopyableId from 'src/components/DataDisplay/CopyableId/CopyableId'
import DotNumberLink from 'src/components/DataDisplay/DotNumberLink/DotNumberLink'
import {
  CARRIER_CURATION_STATUS_LABEL,
  CarrierCurationStatus,
} from 'src/graphql/types/carriers'
import { formatDateAbsolute } from 'src/lib/formatters'
import { preferredPlatform, voeProviderLabel } from 'src/lib/voeProvider.utils'

import type { CarrierDetailRecord } from '../carrier.types'
import { carrierName, contactCountLabel } from '../carrier.utils'

export interface CarrierSummaryProps {
  carrier: CarrierDetailRecord
  /** Controls that act on the whole carrier, shown on the right of the header. */
  actions?: ReactNode
}

const CURATION_BADGE_COLOR: Partial<Record<CarrierCurationStatus, string>> = {
  [CarrierCurationStatus.REVIEWED]: 'blue',
  [CarrierCurationStatus.LOCKED]: 'orange',
}

/**
 * Who set the status and when, for the badge tooltip. The note comes first
 * because it is the part a colleague actually needs.
 */
export const curationTooltip = (carrier: CarrierDetailRecord): string => {
  const parts: string[] = []
  if (carrier.curationNote?.trim()) parts.push(carrier.curationNote.trim())
  const by = [
    carrier.curationChangedBy?.firstName,
    carrier.curationChangedBy?.lastName,
  ]
    .filter(Boolean)
    .join(' ')
  const when = carrier.curationChangedAt
    ? formatDateAbsolute(carrier.curationChangedAt)
    : null
  if (by || when) {
    parts.push(
      ['Set', by && `by ${by}`, when && `on ${when}`].filter(Boolean).join(' ')
    )
  }
  return parts.join(' — ')
}

/**
 * The identity block at the top of the carrier view: what it is called, and one
 * muted line of record facts. Everything else — including the legal name — is a
 * field in the sections below, so it is not repeated here.
 */
const CarrierSummary = ({ carrier, actions }: CarrierSummaryProps) => {
  const curationColor =
    CURATION_BADGE_COLOR[carrier.curationStatus as CarrierCurationStatus]
  const platform = preferredPlatform(carrier.verificationPlatforms)

  return (
    <div className="flex flex-wrap items-start justify-between gap-3">
      <div className="flex flex-col gap-1.5">
        <div className="flex flex-wrap items-center gap-3">
          {/* The carrier is the subject of this route, so its name is the page
              heading — the sections below are its h2s. */}
          <h1 className="text-xl font-semibold text-doubleNickel-gray-900">
            {carrierName(carrier)}
          </h1>
          {carrier.dotNumber && (
            <DotNumberLink
              dotNumber={carrier.dotNumber}
              className="no-underline"
            >
              <Badge
                size="sm"
                variant="light"
                color="gray"
                className="cursor-pointer"
              >
                DOT {carrier.dotNumber}
              </Badge>
            </DotNumberLink>
          )}
          {/* The platform a recruiter would be sent to. Filled and orange when
              that is the only way in, so it reads like the Locked badge — both
              mean "do not go around this". */}
          {platform && (
            <Badge size="sm" variant="light">
              {voeProviderLabel(platform.provider)}
            </Badge>
          )}
          {carrier.platformOnly && (
            <Badge size="sm" variant="filled" color="orange">
              Platform only
            </Badge>
          )}
          {/* Unreviewed is the default and says nothing worth a badge. */}
          {curationColor && (
            <Tooltip
              label={curationTooltip(carrier) || 'No note recorded'}
              withArrow
              multiline
              w={280}
              events={{ hover: true, focus: true, touch: true }}
            >
              <Badge size="sm" variant="filled" color={curationColor}>
                {
                  CARRIER_CURATION_STATUS_LABEL[
                    carrier.curationStatus as CarrierCurationStatus
                  ]
                }
              </Badge>
            </Tooltip>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-1.5 text-xs text-doubleNickel-gray-400">
          <span
            className={
              carrier.contactCount === 0
                ? 'font-medium text-doubleNickel-warning-700'
                : 'text-doubleNickel-gray-600'
            }
          >
            {contactCountLabel(carrier.contactCount)}
          </span>
          <span>·</span>
          <span>
            in the directory since {formatDateAbsolute(carrier.createdAt)}
          </span>
          <span>·</span>
          <span>updated {formatDateAbsolute(carrier.updatedAt)}</span>
          <CopyableId value={carrier.carrierId} label="Carrier ID" />
        </div>
      </div>

      {actions}
    </div>
  )
}

export default CarrierSummary
